import { Controller } from "@hotwired/stimulus"
import { triggerHaptic } from "../src/trigger_native_haptic"

// Routine Review's order screen. It opens on the routine as it is today,
// every line running straight across. Once the screen has landed (its
// sentence and rows in, announced by routine-review:entered) and the reader
// has had a beat to take in today's order, the steps that change slide into
// place and their lines bend to follow. It plays again each time the
// screen is shown.
const LINE_FROM_X = 44
const LINE_TO_X = 100
const PULL_X = 28
const ROW_CENTER_PX = 20
const PAUSE_MS = 600
const MOVE_MS = 900

const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2)

export default class extends Controller {
  static targets = ["line", "row"]
  static values = { pitch: Number }

  connect() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  disconnect() {
    this.stop()
  }

  entered(event) {
    this.stop()
    if (!event.target.contains(this.element)) return

    this.reset()
    if (this.reducedMotion) return this.settle()

    this.timer = setTimeout(() => this.play(), (event.detail?.settled || 0) + PAUSE_MS)
  }

  play() {
    this.element.dataset.moving = ""
    const start = performance.now()
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / MOVE_MS)
      this.draw(ease(progress))
      if (progress < 1) {
        this.frame = requestAnimationFrame(tick)
      } else {
        this.settle()
        triggerHaptic("light")
      }
    }
    this.frame = requestAnimationFrame(tick)
  }

  reset() {
    delete this.element.dataset.moving
    delete this.element.dataset.done
    this.draw(0)
  }

  settle() {
    this.draw(1)
    this.element.dataset.moving = ""
    this.element.dataset.done = ""
  }

  stop() {
    clearTimeout(this.timer)
    cancelAnimationFrame(this.frame)
  }

  // Each row and line `progress` of the way from today's slot to its new one.
  draw(progress) {
    this.lineTargets.forEach((line) => {
      const from = this.center(line.dataset.from)
      const to = from + (this.center(line.dataset.to) - from) * progress
      line.setAttribute(
        "d",
        `M${LINE_FROM_X} ${from} C${LINE_FROM_X + PULL_X} ${from} ${LINE_TO_X - PULL_X} ${to} ${LINE_TO_X} ${to}`
      )
    })
    this.rowTargets.forEach((row) => {
      const offset = (Number(row.dataset.to) - Number(row.dataset.from)) * this.pitchValue * progress
      row.style.transform = offset ? `translateY(${offset}px)` : ""
    })
  }

  center(slot) {
    return ROW_CENTER_PX + Number(slot) * this.pitchValue
  }
}
